import React, { useEffect, useMemo, useState } from 'react';
import { Socket } from 'socket.io-client';

interface ItemDefinition {
  id: string;
  name: string;
  description: string;
  type: 'weapon' | 'armor' | 'accessory' | 'consumable' | 'material';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  equipSlot?: string;
  stats?: Record<string, number>;
  buyPrice: number;
  sellPrice: number;
  icon: string;
}

interface AmbientTradeItem {
  itemId: string;
  quantity: number;
  price: number;
  name: string;
  item: ItemDefinition | null;
}

interface AmbientTradePanelProps {
  socket: Socket | null;
  isOpen: boolean;
  onClose: () => void;
  pieces: number;
  ambientId: string;
  traderName: string;
  items: AmbientTradeItem[];
}

const RARITY_COLORS: Record<string, string> = {
  common: '#9e9e9e',
  uncommon: '#4caf50',
  rare: '#2196f3',
  epic: '#9c27b0',
  legendary: '#ff9800',
};

const renderItemIcon = (icon: string | undefined, name: string): JSX.Element => {
  const publicUrl: string = process.env.PUBLIC_URL || '';
  const resolvedSrc = icon && icon.startsWith('/assets')
    ? `${publicUrl}${icon}`
    : icon && (icon.startsWith('http://') || icon.startsWith('https://'))
    ? icon
    : `${publicUrl}/assets/unknown.png`;

  return (
    <img
      src={resolvedSrc}
      alt={name}
      className="item-img"
      style={{ width: 32, height: 32, objectFit: 'contain' }}
      onError={(e) => {
        const target = e.target as HTMLImageElement;
        if (target.src !== `${publicUrl}/assets/unknown.png`) {
          target.onerror = null;
          target.src = `${publicUrl}/assets/unknown.png`;
        }
      }}
    />
  );
};

export const AmbientTradePanel: React.FC<AmbientTradePanelProps> = ({
  socket,
  isOpen,
  onClose,
  pieces,
  ambientId,
  traderName,
  items,
}): React.ReactElement | null => {
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  useEffect(() => {
    if (!socket) return;

    const handleError = (data: { message: string }): void => {
      setMessage(data.message);
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
    };

    const handleSuccess = (data: { message: string }): void => {
      setMessage(data.message);
      setMessageType('success');
      setTimeout(() => setMessage(''), 2500);
    };

    socket.on('ambient:tradeError', handleError);
    socket.on('ambient:buySuccess', handleSuccess);

    return () => {
      socket.off('ambient:tradeError', handleError);
      socket.off('ambient:buySuccess', handleSuccess);
    };
  }, [socket]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (isOpen && e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => a.price - b.price);
  }, [items]);

  if (!isOpen) return null;

  return (
    <div className="shop-overlay" onClick={onClose}>
      <div className="shop-panel" onClick={(e) => e.stopPropagation()}>
        <div className="shop-panel-header">
          <h2>{traderName}</h2>
          <div className="shop-header-right">
            <span style={{ color: '#ffd700', fontSize: '14px' }}>{pieces.toLocaleString()}₱</span>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        {message && (
          <div className={`shop-message ${messageType === 'error' ? 'error' : ''}`}>
            {message}
          </div>
        )}

        <div className="restock-info">
          <span>Click an item to buy</span>
        </div>

        <div className="shop-content">
          <div className="shop-items">
            {sortedItems.length === 0 ? (
              <div className="empty-message">Nothing to trade</div>
            ) : (
              sortedItems.map((entry) => {
                const def = entry.item;
                const displayName = def?.name || entry.name || entry.itemId;
                const rarity = def?.rarity || 'common';
                const rarityColor = RARITY_COLORS[rarity] || RARITY_COLORS.common;
                const canAfford = pieces >= entry.price;

                return (
                  <div
                    key={entry.itemId}
                    className={`shop-item ${!canAfford ? 'cannot-afford' : ''}`}
                    style={{ borderLeftColor: rarityColor, borderLeftWidth: '3px' }}
                  >
                    <div className="item-icon-wrapper">
                      {renderItemIcon(def?.icon, displayName)}
                    </div>

                    <div className="item-main">
                      <div className="item-info">
                        <span className="item-name" style={{ color: rarityColor }}>
                          {displayName}
                        </span>
                        <div className="item-price-stock">
                          <span className="item-price">₱{entry.price.toLocaleString()}</span>
                          <span className="item-stock">Stock: {entry.quantity}</span>
                        </div>
                        {def?.description && (
                          <div style={{ color: '#9ca3af', fontSize: 11, marginTop: 6, lineHeight: 1.2 }}>
                            {def.description}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="item-actions">
                      <button
                        className="buy-btn"
                        disabled={!canAfford || entry.quantity <= 0}
                        onClick={() => {
                          socket?.emit('ambient:buy', { ambientId, itemId: entry.itemId, quantity: 1 });
                        }}
                      >
                        Buy
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
